import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import Sidebar from '../components/Sidebar';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Calendar, MapPin, Users, Trash2 } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { useAuth } from '../context/AuthContext';

const Events = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const { data } = await api.get('/events');
      setEvents(data);
    } catch (error) {
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const handleRSVP = async (eventId) => {
    try {
      await api.post(`/events/${eventId}/rsvp`);
      fetchEvents();
      toast.success('RSVP updated');
    } catch (error) {
      toast.error('Failed to update RSVP');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Delete this event? This action cannot be undone.')) return;

    try {
      await api.delete(`/admin/events/${eventId}`);
      toast.success('Event deleted');
      fetchEvents();
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-12 gap-6">
          <div className="hidden lg:block lg:col-span-3 sticky top-24 h-fit">
            <Sidebar />
          </div>

          <div className="col-span-12 lg:col-span-9" data-testid="events-container">
            <div className="mb-6">
              <h1 className="font-oswald text-3xl font-bold text-[#1E3A8A] uppercase tracking-tight mb-2">
                Events & Announcements
              </h1>
              <p className="text-slate-600">Stay updated with GBA community events</p>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <p className="text-slate-500">Loading events...</p>
              </div>
            ) : events.length === 0 ? (
              <div className="bg-white rounded-md p-12 text-center">
                <p className="text-slate-500">No upcoming events</p>
              </div>
            ) : (
              <div className="space-y-6">
                {events.map((event) => (
                  <Card
                    key={event.id}
                    className="bg-white border border-slate-100 shadow-sm hover:shadow-md transition-all overflow-hidden"
                    data-testid="event-card"
                  >
                    {event.banner_url && (
                      <img
                        src={event.banner_url}
                        alt={event.title}
                        className="w-full h-48 object-cover"
                      />
                    )}
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h2 className="font-oswald text-2xl font-bold text-[#1E3A8A] uppercase mb-2">
                            {event.title}
                          </h2>
                          {event.is_pinned && (
                            <Badge className="bg-[#22C55E] text-white mb-2">
                              Featured
                            </Badge>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteEvent(event.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid="delete-event-button"
                          title="Delete Event (Admin Only)"
                        >
                          <Trash2 className="h-5 w-5" />
                        </Button>
                      </div>

                      <p className="text-slate-700 mb-4">{event.description}</p>

                      <div className="flex flex-wrap gap-4 text-sm text-slate-600 mb-4">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4" />
                          <span>{format(new Date(event.date), 'PPP')}</span>
                        </div>
                        {event.location && (
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4" />
                            <span>{event.location}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4" />
                          <span>{event.rsvps?.length || 0} attending</span>
                        </div>
                      </div>

                      <Button
                        onClick={() => handleRSVP(event.id)}
                        className={`${
                          event.rsvps?.includes(localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')).id : '')
                            ? 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                            : 'bg-[#22C55E] hover:bg-[#16A34A] text-white'
                        }`}
                        data-testid="rsvp-button"
                      >
                        {event.rsvps?.includes(localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')).id : '')
                          ? 'Going'
                          : 'RSVP'}
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Events;
